import { useEffect, useState, useRef } from 'react';
import { auth, db } from "../firebase"; 
import { collection, query, where, doc, getDoc, deleteDoc, updateDoc, onSnapshot, deleteField } from "firebase/firestore";
import { notifyCompanyStaff, notifyStaff } from '../functions/classes';
import { onAuthStateChanged } from "firebase/auth";
import { AlertStack, NoResultBox, ConfirmationModal } from '../functions/ui';
import { PiHourglassMediumFill, PiClockUserFill, PiToolbox, PiTrashBold, PiClipboardTextBold, PiHash, PiSealCheckBold, PiXCircleBold } from "react-icons/pi";
import { MdOutlinePerson } from "react-icons/md";
import { BsCalendar2Date } from "react-icons/bs";
import { GrStatusGood } from "react-icons/gr";
import { SlLocationPin } from "react-icons/sl";
import { CiBookmarkCheck } from "react-icons/ci";
import { GiSkills } from "react-icons/gi";
import { TbClockHour10, TbTimeDuration15 } from "react-icons/tb";
import { Divider } from 'antd';
import { addRippleEffect } from '../functions/classes';
import '../webapp.css';

const ShiftInfo = () => {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [staffMap, setStaffMap] = useState({});

  // State for context menu
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, shift: null });
  // State for delete confirmation modal
  const [deleteModal, setDeleteModal] = useState({ visible: false, shift: null });
  // State for unassign confirmation modal
  const [unassignModal, setUnassignModal] = useState({ visible: false, shift: null });
  
  // Use an array for alerts
  const [alerts, setAlerts] = useState([]);
  const [clockOutModal, setClockOutModal] = useState({ visible: false, shift: null });
  const [timeDoneInput, setTimeDoneInput] = useState("");
  const [timeDoneError, setTimeDoneError] = useState("");
  const menuRef = useRef(null);

  useEffect(() => {
    let unsubscribeAuth;
    let unsubscribeShifts;
    unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      setLoading(true);
      if (!user) {
        setShifts([]);
        setLoading(false);
        return;
      }
      const shiftsQuery = query(
        collection(db, "shifts"),
        where("companyID", "==", user.uid)
      );
      unsubscribeShifts = onSnapshot(shiftsQuery, (shiftsSnap) => {
        const results = shiftsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setShifts(results);
        setLoading(false);
      }, (error) => {
        console.error("Error:", error);
        setShifts([]);
        setLoading(false);
      });
    });
    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeShifts) unsubscribeShifts();
    };
  }, []);

  // Helper: Concatenate address fields
  const getFullAddress = (shift) => {
    const parts = [
      shift.streetNumber,
      shift.streetName,
      shift.city,
      shift.province,
      shift.country,
      shift.postalCode
    ];
    return parts.filter(Boolean).join(", ");
  };

  // Helper: Status logic
  const getStatus = (shift) => {
    switch (shift.status) {
      case 'pending':
        return { label: "Pending", background: "#bdbdbd" }; // grey
      case 'accepted':
        return { label: "Accepted", background: "#a0522d" }; // brown
      case 'in-progress':
        return { label: "In-progress", background: "#fbbf24" }; // yellow
      case 'completed':
        return { label: "Completed", background: "#10b981" }; // green
      default:
        return { label: "TBD", background: "#6b7280" };
    }
  };

  // Fetch staff info for all unique acceptedBy UIDs
  useEffect(() => {
    const fetchStaff = async () => {
      const uids = Array.from(new Set(shifts.map(s => s.acceptedBy).filter(Boolean)));
      const newStaffMap = { ...staffMap };
      for (const uid of uids) {
        if (!newStaffMap[uid]) {
          try {
            const userRef = doc(db, "users", uid);
            const userSnap = await getDoc(userRef);
            if (userSnap.exists()) {
              const data = userSnap.data();
              newStaffMap[uid] = `${data.firstName || "-"} ${data.lastName || "-"}`.trim() || "N/A";
            } else {
              newStaffMap[uid] = "N/A";
            }
          } catch {
            newStaffMap[uid] = "N/A";
          }
        }
      }
      setStaffMap(newStaffMap);
    };
    if (shifts.length > 0) fetchStaff();
    // eslint-disable-next-line
  }, [shifts]);

  // Context menu handlers
  const handleContextMenu = (e, shift) => {
    e.preventDefault();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, shift });
  };
  const closeContextMenu = () => setContextMenu({ ...contextMenu, visible: false });

  // Open delete confirmation modal
  const handleDeleteShift = (shift) => {
    closeContextMenu();
    setDeleteModal({ visible: true, shift });
  };

  // Confirm delete shift
  const confirmDeleteShift = async () => {
    const shift = deleteModal.shift;
    setDeleteModal({ visible: false, shift: null });
    if (!shift) return;
    try {
      await deleteDoc(doc(db, "shifts", shift.id));
      setShifts(shifts.filter(s => s.id !== shift.id));
      showAlert('success', 'Shift deleted successfully');
    } catch (err) {
      showAlert('error', 'Failed to delete shift');
    }
  };
  const cancelDeleteShift = () => setDeleteModal({ visible: false, shift: null });

  // Copy ShiftID to clipboard
  const handleCopyShiftID = async (shift) => {
    try {
      await navigator.clipboard.writeText(shift.id);
      showAlert('success', 'Shift ID successfully copied');
    } catch {
      showAlert('error', 'Failed to copy Shift ID');
    }
    closeContextMenu();
  };

  // Unassign handler (called after confirmation)
  const handleUnassign = async (shift) => {
    setUnassignModal({ visible: false, shift: null });
    closeContextMenu();
    if (!shift.acceptedBy) {
      showAlert('error', 'Shift is not assigned');
      return;
    }
    try {
      const shiftRef = doc(db, "shifts", shift.id);
      // Update shift doc: delete acceptedBy and acceptedAt, and reset status
      await updateDoc(shiftRef, {
        status: 'pending',
        acceptedBy: deleteField(),
        acceptedAt: deleteField()
      });
      // Update staff doc: set onShift to false and delete onShiftID
      if (shift.acceptedBy) {
        const staffRef = doc(db, "users", shift.acceptedBy);
        await updateDoc(staffRef, {
          onShift: false,
          onShiftID: deleteField()
        });
      }
      setShifts(shifts.map(s => {
        if (s.id === shift.id) {
          const updated = { ...s };
          delete updated.acceptedBy;
          delete updated.acceptedAt;
          return updated;
        }
        return s;
      }));
      showAlert('success', 'Shift unassigned and staff notified');
      // Notify all staff of company except the unassigned staff
      await notifyCompanyStaff(shift.companyID, 'A shift has been dropped and is now available', 'New Shift Available', [shift.acceptedBy], true);
      // Notify the staff who was unassigned
      await notifyStaff(shift.acceptedBy, 'You have been unassigned from a shift', 'Shift Unassigned');
    } catch (err) {
      showAlert('error', 'Failed to unassign shift');
    }
  };

  // Clock Staff Out handler
  const handleClockOut = (shift) => {
    closeContextMenu();
    
    const status = shift.status;

    if (status === 'pending' || status === 'accepted') {
      showAlert('error', 'Staff has not clocked in');
      return;
    }
    if (status === 'completed') {
      showAlert('error', 'Staff already clocked out');
      return;
    }
    // Only allow modal for 'in-progress' status
    if (status === 'in-progress') {
      setTimeDoneInput("");
      setTimeDoneError("");
      setClockOutModal({ visible: true, shift });
    }
  };

  const confirmClockOut = async () => {
    const shift = clockOutModal.shift;
    if (!shift) return;
    if (!timeDoneInput || timeDoneInput.trim() === "") {
      setTimeDoneError("Time Done cannot be empty");
      return;
    }
    setClockOutModal({ visible: false, shift: null });
    setTimeDoneError("");
    try {
      const shiftRef = doc(db, "shifts", shift.id);
      await updateDoc(shiftRef, {
        clockedoutAt: new Date().toISOString(),
        clockedOut: true,
        timedone: timeDoneInput
      });
      // Set onShift to false for the staff user
      if (shift.acceptedBy) {
        const staffRef = doc(db, "users", shift.acceptedBy);
        await updateDoc(staffRef, { onShift: false });
        // Send push notification to staff
        await notifyStaff(shift.acceptedBy, 'You have been clocked out by your employer', 'Clocked Out');
      }
      setShifts(shifts.map(s => s.id === shift.id ? { ...s, 
        clockedoutAt: new Date().toISOString(), 
        timedone: timeDoneInput 
      } : s));
      showAlert('success', 'Staff clocked out successfully');
    } catch (err) {
      showAlert('error', 'Failed to clock staff out');
    }
  };
  const cancelClockOut = () => {
  setClockOutModal({ visible: false, shift: null });
  setTimeDoneError("");
  };

  // Close context menu on click outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (contextMenu.visible && menuRef.current && !menuRef.current.contains(event.target)) {
        setContextMenu({ ...contextMenu, visible: false });
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [contextMenu]);

  // Helper to show a new alert (with unique id)
  const showAlert = (type, message) => {
    const id = Date.now() + Math.random();
    setAlerts(prev => [...prev, { id, type, message }]);
  };
  // Remove alert by id
  const handleAlertClose = (id) => {
    setAlerts(prev => prev.filter(alert => alert.id !== id));
  };

  return (
    <div className='dashboard-box'>
      <h2>Shift Details</h2>
      <p className='small-text'>Manage shifts and get insights with KierepAI</p>
      <Divider className='dash-divider'/>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="second-table-wrapper">
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th><div><PiHash className="table-icon icon-grey" /> ShiftID</div></th>
                  <th><div><BsCalendar2Date className="table-icon icon-blue" /> Date created</div></th>
                  <th><div><GrStatusGood className="table-icon icon-grey" /> Status</div></th>
                  <th><div><MdOutlinePerson className="table-icon icon-yellow" /> Staff</div></th>
                  <th><div><SlLocationPin className="table-icon icon-blue" /> Shift location</div></th>
                  <th><div><CiBookmarkCheck className="table-icon icon-grey" /> Time accepted</div></th>
                  <th><div><TbTimeDuration15 className="table-icon icon-blue" /> Duration</div></th>
                  <th><div><PiToolbox className="table-icon icon-yellow" /> Skill</div></th>
                  <th><div><GiSkills className="table-icon icon-grey" /> Staff type</div></th>
                  <th><div><PiHourglassMediumFill className="table-icon icon-blue" /> Hours done</div></th>
                  <th><div><PiClockUserFill className="table-icon icon-yellow" /> Time clockedin</div></th>
                  <th><div><TbClockHour10 className="table-icon icon-grey" /> Time clockedout</div></th>
                </tr>
              </thead>
            </table>
            {shifts.length > 0 ? (
              <div className="tbody-overflow">
                <table className="table">
                  <tbody>
                    {[...shifts]
                      .sort((a, b) => {
                        // Sort by dateCreated descending (newest first)
                        const aTime = a.dateCreated ? new Date(a.dateCreated).getTime() : 0;
                        const bTime = b.dateCreated ? new Date(b.dateCreated).getTime() : 0;
                        return bTime - aTime;
                      })
                      .map((shift) => {
                        const status = getStatus(shift);
                        return (
                          <tr key={shift.id} onContextMenu={e => { handleContextMenu(e, shift); }}>
                            <td>{shift.id}</td>
                            <td>{shift.dateCreated ? new Date(shift.dateCreated).toLocaleString() : "-"}</td>
                            <td>
                              <span style={{
                                color: '#fff',
                                background: status.background,
                                borderRadius: 8,
                                padding: "2px 8px",
                              }}>{status.label}</span>
                            </td>
                            <td>{shift.acceptedBy ? (staffMap[shift.acceptedBy] || "Loading...") : "-"}</td>
                            <td>{getFullAddress(shift)}</td>
                            <td>{shift.acceptedAt ? new Date(shift.acceptedAt).toLocaleString() : "-"}</td> <td>
                              {(() => {
                                const parts = [];
                                if (shift.days && shift.days !== 0) parts.push(`${shift.days} day${shift.days === 1 ? '' : 's'}`);
                                if (shift.hours && shift.hours !== 0) parts.push(`${shift.hours} hr${shift.hours === 1 ? '' : 's'}`);
                                if (shift.minutes && shift.minutes !== 0) parts.push(`${shift.minutes} min${shift.minutes === 1 ? '' : 's'}`);
                                return parts.length > 0 ? parts.join(' ') : '-';
                              })()}
                            </td>
                            <td>{shift.skill || "-"}</td>
                            <td>{shift.staffType || "-"}</td>
                            <td>{shift.timedone ? shift.timedone : "-"}</td>
                            <td>{shift.clockedinAt ? new Date(shift.clockedinAt).toLocaleString() : "Not Clocked In"}</td>
                            <td>{shift.clockedoutAt ? new Date(shift.clockedoutAt).toLocaleString() : "Not Clocked Out"}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="error-div">
                <NoResultBox />
              </div>
            )}
          </div>
        </div>
      )}
      {/* Context Menu */}
      {contextMenu.visible && (
        <div
          className="menu-box"
          style={{top: contextMenu.y, left: contextMenu.x, position: 'fixed'}}
          ref={menuRef}
        >
          <div className='menu-box-div' onClick={e => { addRippleEffect(e); setUnassignModal({ visible: true, shift: contextMenu.shift }); closeContextMenu(); }}><PiXCircleBold className="profile-menu-icon" /><span>Unassign</span></div>
          <div className='menu-box-div' onClick={e => { addRippleEffect(e); handleClockOut(contextMenu.shift); }}><PiSealCheckBold className="profile-menu-icon" /><span>Clock Staff Out</span></div>
          <div className='menu-box-div' onClick={e => { addRippleEffect(e); handleCopyShiftID(contextMenu.shift); }}><PiClipboardTextBold className="profile-menu-icon" /><span>Copy ShiftID</span></div>
          <div className='menu-divider-div'><Divider className='menu-divider'/></div>
          <div className='menu-box-div red-menu' onClick={e => { addRippleEffect(e); handleDeleteShift(contextMenu.shift); }}><PiTrashBold className="profile-menu-icon" /><span>Delete</span></div>
        </div>
      )}
      {/* Unassign Confirmation Modal */}
      <ConfirmationModal
        open={unassignModal.visible}
        title="Unassign Staff"
        message="Are you sure you want to unassign this staff from the shift?"
        confirmText="Unassign"
        cancelText="Cancel"
        onConfirm={() => handleUnassign(unassignModal.shift)}
        onCancel={() => setUnassignModal({ visible: false, shift: null })}
        isSubmitting={loading}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteModal.visible}
        title="Delete Shift"
        message="Are you sure you want to delete this shift? This action cannot be undone"
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteShift}
        onCancel={cancelDeleteShift}
        isSubmitting={loading}
      />

      {/* Clock Out Modal with time done input */}
      {clockOutModal.visible && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ gap: 10, display: 'flex', flexDirection: 'column' }}>
            <h3>Clock Staff Out</h3>
            <div className="small-text">Time Done</div>
            <div>
              <input
                type="text"
                value={timeDoneInput}
                onChange={e => setTimeDoneInput(e.target.value)}
              />
              {timeDoneError && <div className="error-message">{timeDoneError}</div>}
            </div>
            <div className="popupbox-actions">
              <button className="popupbox-btn popupbox-btn-confirm" onClick={confirmClockOut}>Submit</button>
              <button className="popupbox-btn popupbox-btn-cancel" onClick={cancelClockOut}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Box */}
      <AlertStack alerts={alerts} onAlertClose={handleAlertClose} />
    </div>
  );
};

export default ShiftInfo;